import { ApiResponse, axiosInstance } from '@/lib/axios';
import { FetchParams, IPagination } from '@/types/general.type';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TChannel } from '../../_shared/types/channel.type';

export type TGetListIntegrationYoutubeResponse = ApiResponse<IPagination<TChannel>>;
export type TGetListIntegrationYoutubeParams = FetchParams;

export const getListIntegrationYoutube = async (
  params: TGetListIntegrationYoutubeParams
): Promise<TGetListIntegrationYoutubeResponse> => {
  const url = '/integration/youtube/org-channel';

  return axiosInstance.get(url, { params });
};

export const GET_LIST_INTEGRATION_YOUTUBE_QUERY_KEY = 'get-list-integration-youtube';

export const getListIntegrationYoutubeQueryOptions = <TData = IPagination<TChannel>>(
  params: TGetListIntegrationYoutubeParams,
  select?: (data: TGetListIntegrationYoutubeResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_INTEGRATION_YOUTUBE_QUERY_KEY,
      {
        page: params?.page,
        limit: params?.limit,
        search: params?.search,
        sortDir: params?.sortDir,
      },
    ],
    queryFn: () => getListIntegrationYoutube(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetListIntegrationYoutubeQuery<TData>(options: {
  params: TGetListIntegrationYoutubeParams;
  queryConfig?: QueryConfig<typeof getListIntegrationYoutubeQueryOptions<TData>>;
  select: (data: TGetListIntegrationYoutubeResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetListIntegrationYoutubeQuery(options: {
  params: TGetListIntegrationYoutubeParams;
  queryConfig?: QueryConfig<typeof getListIntegrationYoutubeQueryOptions<TGetListIntegrationYoutubeResponse>>;
}): ReturnType<typeof useQuery<TGetListIntegrationYoutubeResponse>>;

// Implementation
export function useGetListIntegrationYoutubeQuery<TData = TGetListIntegrationYoutubeResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListIntegrationYoutubeParams;
  queryConfig?: QueryConfig<typeof getListIntegrationYoutubeQueryOptions<TData | TGetListIntegrationYoutubeResponse>>;
  select?: (data: TGetListIntegrationYoutubeResponse) => TData;
}) {
  return useQuery({
    ...getListIntegrationYoutubeQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<TData extends TGetListIntegrationYoutubeResponse ? TGetListIntegrationYoutubeResponse : TData>
  >;
}
